import axios from 'axios';
import { CognitoUserPool, CognitoRefreshToken } from 'amazon-cognito-identity-js';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});

// ── Token helpers ────────────────────────────────────────────────────────────

function decodeToken(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  const payload = decodeToken(token);
  if (!payload) return true;
  return Date.now() >= payload.exp * 1000;
}

/**
 * Returns true if the token will expire within the next `bufferMs` milliseconds.
 * Default buffer: 5 minutes — triggers refresh before actual expiration.
 */
function isTokenExpiringSoon(token, bufferMs = 5 * 60 * 1000) {
  const payload = decodeToken(token);
  if (!payload) return true;
  return Date.now() >= (payload.exp * 1000) - bufferMs;
}

function logout() {
  sessionStorage.removeItem('idToken');
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.location.href = '/login';
}

// ── Token refresh via Cognito SDK ────────────────────────────────────────────

let refreshPromise = null;

/**
 * Refreshes the Cognito session using the stored refresh token.
 * De-duplicates concurrent calls — only one refresh runs at a time.
 */
function refreshSession() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = new Promise((resolve) => {
    const refreshTokenStr = sessionStorage.getItem('refreshToken');
    if (!refreshTokenStr) {
      refreshPromise = null;
      return resolve(false);
    }

    const poolData = {
      UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
    };
    const userPool = new CognitoUserPool(poolData);
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      refreshPromise = null;
      return resolve(false);
    }

    const refreshToken = new CognitoRefreshToken({ RefreshToken: refreshTokenStr });

    cognitoUser.refreshSession(refreshToken, (err, session) => {
      refreshPromise = null;

      if (err) {
        console.warn('Token refresh failed:', err.message);
        return resolve(false);
      }

      // Store new tokens
      sessionStorage.setItem('idToken', session.getIdToken().getJwtToken());
      sessionStorage.setItem('accessToken', session.getAccessToken().getJwtToken());
      // Refresh token stays the same (Cognito doesn't rotate it by default)

      resolve(true);
    });
  });

  return refreshPromise;
}

// ── Request interceptor: refresh proactively if token is expiring soon ───────

api.interceptors.request.use(async (config) => {
  let idToken = sessionStorage.getItem('idToken') || localStorage.getItem('idToken');

  // No token: let the request through (public endpoints)
  if (!idToken) return config;

  // Token already fully expired and no refresh token — logout
  if (isTokenExpired(idToken)) {
    const refreshTokenStr = sessionStorage.getItem('refreshToken');
    if (!refreshTokenStr) {
      logout();
      return Promise.reject(new axios.Cancel('Token expirado'));
    }

    // Try to refresh
    const refreshed = await refreshSession();
    if (!refreshed) {
      logout();
      return Promise.reject(new axios.Cancel('Token expirado'));
    }

    idToken = sessionStorage.getItem('idToken');
  } else if (isTokenExpiringSoon(idToken)) {
    // Token still valid but expiring soon — refresh in background
    refreshSession().catch(() => {});
    // Use current (still valid) token for this request
  }

  config.headers.Authorization = `Bearer ${idToken}`;
  return config;
});

// ── Response interceptor: retry on 401 after refresh ─────────────────────────

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't retried yet, attempt a refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshed = await refreshSession();
      if (refreshed) {
        // Retry the original request with the new token
        const newToken = sessionStorage.getItem('idToken');
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }

      // Refresh failed — logout
      logout();
    }

    return Promise.reject(error);
  }
);

export default api;
